import { Box, Grid2 } from '@mui/material';
import { getRatesOptions, Price, PriceStatus } from '@utriper/core';
import { Autocomplete, CharacterCounter, SelectComponent, TextInput } from '@utriper/core-ui';
import { usePriceGroup } from 'core/contexts/priceGroup';
import { useMemo } from 'react';
import { Field, Form } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import ActionButtons from '../ActionButtons';
import Title from 'modules/Pricing/components/Title';
import { Label } from '../../styles';

const PriceGroupForm = ({ priceGroupId, handleClose = () => {} }) => {
    const { i18n, t } = useTranslation();
    const translate = (key) => t(`price_group_id.price_group_form.${key}`);
    const { priceGroup, updatePriceGroupData, submittingPriceGroup } = usePriceGroup();
    const statusOptions = useMemo(
        () =>
            PriceStatus.getIds().map((status) => ({
                id: status,
                name: PriceStatus.getTranslatedName(PriceStatus.status, status, i18n.language),
            })),
        [i18n.language]
    );
    const priceTypeOptions = useMemo(
        () =>
            Price.getIds().map((priceType) => ({
                id: priceType,
                name: Price.getTranslatedName(Price.types, priceType, i18n.language),
            })),
        [i18n.language]
    );
    const currencyOptions = getRatesOptions();
    const initialCurrencyValue = useMemo(() => currencyOptions.find((option) => option?.name === priceGroup?.currency), [currencyOptions, priceGroup]);

    const validateForm = (values) => {
        const errors = {};
        if (!values.name) {
            errors.name = translate('validate_form.required');
        }
        if (values?.name?.length > 255) {
            errors.name = translate('validate_form.length');
        }
        if (values?.description && values?.description?.length > 255) {
            errors.description = translate('validate_form.length');
        }
        if (!Number.isInteger(values?.status)) {
            errors.status = translate('validate_form.required');
        }
        if (!Number.isInteger(values?.type)) {
            errors.type = translate('validate_form.required');
        }
        if (!values?.currency) {
            errors.currency = translate('validate_form.required');
        }

        return errors;
    };

    const handleSubmit = async (values) => {
        await updatePriceGroupData(priceGroupId, values);
        handleClose();
    };

    return (
        <Form
            key={`price-group-form-${priceGroupId}`}
            onSubmit={handleSubmit}
            initialValues={{ ...priceGroup, currency: initialCurrencyValue }}
            validate={validateForm}
            render={({ values, errors, handleSubmit, form }) => (
                <form key={`price-group-form-${priceGroupId}`} onSubmit={handleSubmit} noValidate>
                    <Box component="section" py={2}>
                        <Title title={translate('title')} description={translate('description')} />

                        {/* Form */}
                        <Grid2 container direction="column" justifyContent="flex-start" alignItems="flex-start" gap="8px" mt="24px">
                            {/* Name */}
                            <Grid2 container width="100%" direction="column" justifyContent="flex-start" alignItems="flex-start" gap="4px">
                                <Label>{translate('form.name')}</Label>
                                <Field
                                    name="name"
                                    component={TextInput}
                                    placeholder={translate('form.name_placeholder')}
                                    disabled={submittingPriceGroup}
                                    slotProps={{
                                        input: {
                                            inputProps: {
                                                maxLength: 255,
                                            },
                                        },
                                    }}
                                />
                                <CharacterCounter length={values?.name?.length ?? 0} />
                            </Grid2>
                            {/* Description */}
                            <Grid2 container width="100%" direction="column" justifyContent="flex-start" alignItems="flex-start" gap="4px">
                                <Label>{translate('form.description')}</Label>
                                <Field
                                    name="description"
                                    component={TextInput}
                                    placeholder={translate('form.description_placeholder')}
                                    multiline
                                    minRows={2}
                                    disabled={submittingPriceGroup}
                                    slotProps={{
                                        input: {
                                            sx: {
                                                '& textarea': {
                                                    resize: 'vertical',
                                                    minHeight: '32px',
                                                    maxHeight: '160px',
                                                    overflow: 'auto',
                                                },
                                            },
                                            inputProps: {
                                                maxLength: 255,
                                            },
                                        },
                                    }}
                                />
                                <CharacterCounter length={values?.description?.length ?? 0} />
                            </Grid2>

                            <Grid2 container width="100%" direction="column" justifyContent="flex-start" alignItems="flex-start" gap="4px">
                                <Label>{translate('form.status')}</Label>
                                <Field name="status" component={SelectComponent} values={statusOptions} disabled={submittingPriceGroup} />
                            </Grid2>

                            {/* Price Type */}
                            <Grid2 container width="100%" direction="column" justifyContent="flex-start" alignItems="flex-start" gap="4px">
                                <Label>{translate('form.price_type')}</Label>
                                <Field name="type" component={SelectComponent} values={priceTypeOptions} displayEmpty label={translate('form.price_type')} hasError={submittingPriceGroup && errors?.type} errorMessage={errors?.type} disabled={submittingPriceGroup} />
                            </Grid2>

                            {/* Currency */}
                            <Grid2 container width="100%" direction="column" justifyContent="flex-start" alignItems="flex-start" gap="4px">
                                <Label>{translate('form.currency')}</Label>
                                <Field name="currency" component={Autocomplete} options={currencyOptions} label={translate('form.currency')} fullWidth disabled={submittingPriceGroup} />
                            </Grid2>
                        </Grid2>

                        <ActionButtons
                            handleSubmit={(e) => {
                                return handleSubmit(e);
                            }}
                            handleClose={(e) => {
                                handleClose();
                                return form.reset(e);
                            }}
                            submitting={submittingPriceGroup}
                            labelNext={translate('update_button')}
                        />
                    </Box>
                </form>
            )}
        />
    );
};

export default PriceGroupForm;
